import {
  OccupancyDatasetBlockModel,
  OccupancyDatasetModel,
  OccupancyDatasetStayModel
} from '../models/occupancy-dashboard.model';
import {
  DashboardBlock,
  DashboardBooking,
  DashboardCalendar,
  DashboardMonth,
  DashboardPortfolio,
  DashboardUnit,
  NightStatus
} from '../ui-models/occupancy-dashboard.ui-model';

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const DAY_MS = 864e5;

/** Display names for Reservation.channel. Anything unlisted is shown as it is stored. */
const CHANNEL_LABELS: Record<string, string> = {
  birdnest: 'Direct',
  airbnb: 'Airbnb',
  booking: 'Booking.com',
  expedia: 'Expedia',
  vrbo: 'Vrbo',
  unknown: 'Unknown'
};

export const BLOCK_REASONS = ['Owner stay', 'Maintenance', 'Other'];

const toUtc = (dayKey: string): number => {
  const [y, m, d] = dayKey.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

const dayIndex = (start: string, dayKey: string): number =>
  Math.round((toUtc(dayKey) - toUtc(start)) / DAY_MS);

const channelLabel = (channel: string): string => CHANNEL_LABELS[channel] || channel;

function buildCalendar(start: string, end: string, reportDate: string): DashboardCalendar {
  const totalDays = dayIndex(start, end);
  const months: DashboardMonth[] = [];
  const monthOfDay = new Int16Array(totalDays);
  const monthFirstDay: number[] = [];
  const monthLength: number[] = [];

  for (let i = 0; i < totalDays; i++) {
    const d = new Date(toUtc(start) + i * DAY_MS);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const last = months[months.length - 1];
    if (!last || last.y !== y || last.m !== m) {
      months.push({
        y,
        m,
        label: `${MONTH_ABBR[m]}${m === 0 ? ' ' + String(y).slice(2) : ''}`,
        long: `${MONTH_ABBR[m]} ${y}`
      });
      monthFirstDay.push(i);
      monthLength.push(0);
    }
    monthOfDay[i] = months.length - 1;
    monthLength[months.length - 1] += 1;
  }

  return {
    start,
    totalDays,
    months,
    monthOfDay,
    monthFirstDay,
    monthLength,
    today: dayIndex(start, reportDate),
    yearEnd: dayIndex(start, `${reportDate.slice(0, 4)}-12-31`)
  };
}

function blockReason(block: OccupancyDatasetBlockModel): string {
  if (block.isOwnerStay) return 'Owner stay';
  if (block.isMaintenance) return 'Maintenance';
  return 'Other';
}

/**
 * Lays the dataset out night by night. Each night gets exactly one state, applied in this order:
 *
 * 1. Listed nights are inventory (FREE); the rest are UNLISTED.
 * 2. A block claims the listed nights it covers. Where two blocks overlap, the earlier-starting one
 *    keeps the night, so a night is never costed twice.
 * 3. A live stay claims every night it covers, over a block or an unlisted day: the guest stayed,
 *    so the night was sold. The block loses that night.
 *
 * Canceled stays claim no nights; they are kept on the unit's bookings for the pace curves only.
 */
function mapToUI(dataset: OccupancyDatasetModel): DashboardPortfolio {
  const calendar = buildCalendar(dataset.window.from, dataset.window.to, dataset.reportDate);
  const { start, totalDays } = calendar;
  const clampRange = (from: string, to: string): [number, number] => [
    Math.max(0, dayIndex(start, from)),
    Math.min(totalDays, dayIndex(start, to))
  ];

  const staysByRoom = groupBy(dataset.stays, (s) => s.roomId);
  const blocksByRoom = groupBy(dataset.blocks, (b) => b.roomId);

  const sources: string[] = [];
  const nationalities: string[] = [];
  const indexOf = (list: string[], value: string): number => {
    let i = list.indexOf(value);
    if (i === -1) {
      list.push(value);
      i = list.length - 1;
    }
    return i;
  };

  const units: DashboardUnit[] = dataset.units.map((u) => {
    const status = new Uint8Array(totalDays).fill(NightStatus.UNLISTED);
    const srcOfDay = new Int8Array(totalDays).fill(-1);
    const natOfDay = new Int16Array(totalDays).fill(-1);
    const rateOfDay = new Float32Array(totalDays);
    const blockOfDay = new Int16Array(totalDays).fill(-1);

    u.listed.forEach((range) => {
      const [a, z] = clampRange(range.from, range.to);
      for (let d = a; d < z; d++) status[d] = NightStatus.FREE;
    });

    const roomBlocks = (blocksByRoom[u.roomId] || [])
      .slice()
      .sort((x, y) => (x.from < y.from ? -1 : x.from > y.from ? 1 : x.id - y.id));
    const blocks: DashboardBlock[] = roomBlocks.map((b, bi) => {
      const [a, z] = clampRange(b.from, b.to);
      for (let d = a; d < z; d++) {
        if (status[d] === NightStatus.FREE) {
          status[d] = NightStatus.BLOCKED;
          blockOfDay[d] = bi;
        }
      }
      return {
        id: String(b.id),
        start: dayIndex(start, b.from),
        nights: dayIndex(b.from, b.to),
        reason: blockReason(b),
        description: b.description,
        costClass: b.costClass,
        owner: b.createdBy || 'Unknown',
        createdAt: b.createdOn ? dayIndex(start, b.createdOn) : null
      };
    });

    const bookings: DashboardBooking[] = (staysByRoom[u.roomId] || []).map(
      (s: OccupancyDatasetStayModel) => {
        const source = channelLabel(s.channel);
        const nationality = s.nationality || 'Unknown';

        if (!s.canceledOn) {
          const src = indexOf(sources, source);
          const nat = indexOf(nationalities, nationality);
          const [a, z] = clampRange(s.from, s.to);
          for (let d = a; d < z; d++) {
            status[d] = NightStatus.SOLD;
            blockOfDay[d] = -1;
            srcOfDay[d] = src;
            natOfDay[d] = nat;
            rateOfDay[d] = 0;
          }
          s.revenue.forEach((r) => {
            const [ra, rz] = clampRange(r.from, r.to);
            for (let d = Math.max(a, ra); d < Math.min(z, rz); d++) rateOfDay[d] += r.perNightEgp;
          });
        }

        return {
          source,
          nationality,
          reservationId: s.reservationId,
          start: dayIndex(start, s.from),
          nights: dayIndex(s.from, s.to),
          bookedAt: dayIndex(start, s.bookedOn),
          canceledAt: s.canceledOn ? dayIndex(start, s.canceledOn) : null
        };
      }
    );

    return {
      status,
      srcOfDay,
      natOfDay,
      rateOfDay,
      blockOfDay,
      bookings,
      blocks,
      id: u.code,
      roomId: u.roomId,
      name: u.name,
      project: u.project || '—',
      area: u.area || '—',
      building: u.building || '—',
      landlord: u.landlord || '—',
      unitType: u.unitType || '—',
      unitTypeGroup: u.unitTypeGroup || '—',
      view: u.view || '—',
      currency: u.currency || '—'
    };
  });

  return { calendar, units, sources, nationalities, reasons: BLOCK_REASONS };
}

function groupBy<T>(rows: T[], key: (row: T) => string): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  rows.forEach((row) => {
    const k = key(row);
    (out[k] = out[k] || []).push(row);
  });
  return out;
}

export default {
  mapToUI
};
